import {
  COLOUR_SOURCE_LABEL,
  selectionsSummary,
  type Job,
  type JobColour,
} from "@roof-hawk/core";

import { Row } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchSelections, type SelectionsData } from "../../lib/selections";

const OTHER = "__other";

/** Everything the card shows: `selectionsSummary` decides, this lays it out. */
function selectionsView(job: Job, data: SelectionsData) {
  const summary = selectionsSummary({
    chosen: job.color_choice ?? null,
    orderLines: data.orderLines,
    sendsEmail: data.sendsEmail,
    estimateLines: data.estimateLines,
  });
  const chosen =
    summary.colour?.source === "chosen" ? summary.colour.colour : "";
  return {
    auto: summary.fromJob,
    chosen,
    picked: summary.picked,
    others: summary.others,
    options: [...new Set([...data.colourOptions, ...(chosen ? [chosen] : [])])],
    empty: !summary.colour,
  };
}

/**
 * What the customer picked, on the Overview (ADR 0304).
 *
 * Jonathon, 2026-10-23: "lysa asked me which color eugene byars selected for
 * the shingles but I put it in the sends email ... the overview screen should
 * show the customer's selections and color choices." The colour is read from
 * the material order, the Sends email or the estimate, whichever says, and the
 * dropdown sets it by hand when none of them does or one of them is wrong.
 * Setting it here never touches a material order.
 *
 * Nothing at all on a job with nothing picked and not yet handed off: a fresh
 * lead has no selections, and an empty card on every one is clutter.
 */
export function SelectionsCard({
  job,
  onPatchJob,
}: {
  job: Job;
  onPatchJob: (patch: Partial<Job>) => Promise<void>;
}) {
  const found = useAsync(() => fetchSelections(job), [job.id, job.sends_email]);
  if (!found.data) return null;
  const view = selectionsView(job, found.data);
  const nothing =
    view.empty && view.picked.length === 0 && view.others.length === 0;
  if (!job.sends_at && nothing) return null;

  async function choose(value: string) {
    if (value === OTHER) {
      const typed = window.prompt(t("Which colour did the customer choose?"));
      if (typed === null || typed.trim() === "") return;
      await onPatchJob({ color_choice: typed.trim() });
      return;
    }
    await onPatchJob({ color_choice: value || null });
  }

  return (
    <div className="block selections-card">
      <h2>{t("Selections")}</h2>
      <ColourRow
        auto={view.auto}
        chosen={view.chosen}
        options={view.options}
        onChoose={(value) => void choose(value)}
      />
      <ListRow label={t("Other colours")} lines={view.others} />
      <ListRow label={t("From the Sends email")} lines={view.picked} />
    </div>
  );
}

function ColourRow({
  auto,
  chosen,
  options,
  onChoose,
}: {
  auto: JobColour | null;
  chosen: string;
  options: readonly string[];
  onChoose: (value: string) => void;
}) {
  return (
    <Row label={t("Colour")}>
      <select
        aria-label={t("Colour")}
        value={chosen}
        onChange={(e) => onChoose(e.target.value)}
      >
        <option value="">
          {auto
            ? `${auto.colour} (${t(COLOUR_SOURCE_LABEL[auto.source])})`
            : t("Not chosen yet")}
        </option>
        {options.map((colour) => (
          <option key={colour} value={colour}>
            {colour}
          </option>
        ))}
        <option value={OTHER}>{t("Other…")}</option>
      </select>
      {chosen && (
        <div className="na-hint">
          {t("Chosen by hand.")}{" "}
          <button
            type="button"
            className="btn-ghost"
            onClick={() => onChoose("")}
          >
            {auto
              ? t("Use {colour} from the job instead", { colour: auto.colour })
              : t("Clear")}
          </button>
        </div>
      )}
      {!chosen && auto?.sendsSays && (
        <div className="config-warn" role="status">
          {t(
            "The material order says {colour}; the Sends email says {sends}.",
            {
              colour: auto.colour,
              sends: auto.sendsSays,
            },
          )}
        </div>
      )}
    </Row>
  );
}

function ListRow({
  label,
  lines,
}: {
  label: string;
  lines: readonly string[];
}) {
  if (lines.length === 0) return null;
  return (
    <Row label={label}>
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </Row>
  );
}
